import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import {
  fmt,
  metricDisplayLabel,
  occupancyPct,
  outletTurns,
  PERF_MEASURE,
  perfConnectSource,
} from '../data/metrics';
import type {
  ConnectedSourceId,
  GuestReviewOutletRow,
  OpsCommandPerformancePayload,
  Outlet,
  OutletPerformanceRow,
  PerformanceMetric,
} from '../types';
import { Delta } from './shared/Delta';
import { ScoreCardHelp } from './shared/ScoreCardHelp';
import { scorecardPeriodDelta, relDelta } from './shared/Trend';

export const OUTLET_SCORE_GROUPS: { id: string; keys: string[]; get label(): string }[] = [
  {
    id: 'demand',
    get label() { return i18n.t('performance:groups.demand'); },
    keys: ['covers', 'reservations', 'noshow', 'cancel'],
  },
  {
    id: 'efficiency',
    get label() { return i18n.t('performance:groups.efficiency'); },
    keys: ['occupancy', 'brainAutoPct', 'turns', 'seatTime'],
  },
  {
    id: 'money',
    get label() { return i18n.t('performance:groups.money'); },
    keys: ['sellitems', 'revenue', 'avgCheck', 'revpash'],
  },
];

export const OUTLET_TABLE_METRIC_KEYS = OUTLET_SCORE_GROUPS.flatMap(g => g.keys);

export const OUTLET_TABLE_HIDDEN_METRICS = new Set(['revpash']);

export const OUTLET_TABLE_INVERT_METRICS = new Set(['noshow', 'cancel', 'rejected']);

export type OutletTrendMetricKind = 'volume' | 'rate' | 'duration' | 'ratio';

export type OutletTrendMeta = {
  importance: number;
  kind: OutletTrendMetricKind;
  /** When true, an increase in the raw value counts as negative momentum. */
  invert?: boolean;
};

/** Importance tier + impact model per metric for period momentum scoring. */
export const OUTLET_TREND_META: Record<string, OutletTrendMeta> = {
  covers: { importance: 3.0, kind: 'volume' },
  reservations: { importance: 2.5, kind: 'volume' },
  rejected: { importance: 1.5, kind: 'volume', invert: true },
  noshow: { importance: 2.0, kind: 'rate', invert: true },
  cancel: { importance: 2.0, kind: 'rate', invert: true },
  occupancy: { importance: 1.8, kind: 'rate' },
  brainAutoPct: { importance: 0.7, kind: 'rate' },
  turns: { importance: 1.0, kind: 'ratio' },
  seatTime: { importance: 0.5, kind: 'duration', invert: true },
  sellitems: { importance: 1.0, kind: 'volume' },
  avgCheck: { importance: 0.9, kind: 'ratio' },
  reviewRating: { importance: 0.8, kind: 'rate' },
  reviewCount: { importance: 0.4, kind: 'volume' },
};

/** @deprecated Use OUTLET_TREND_META importance values. */
export const OUTLET_TREND_WEIGHTS: Record<string, number> = Object.fromEntries(
  Object.entries(OUTLET_TREND_META).map(([key, meta]) => [key, meta.importance]),
);

const OUTLET_TREND_VOLUME_FLOOR = 20;
const OUTLET_TREND_DEMAND_KEYS = new Set(['covers', 'reservations']);

export const OUTLET_TREND_MIN_COVERS = 80;
export const OUTLET_TREND_MIN_METRICS = 2;
export const OUTLET_TREND_MIN_ELIGIBLE = 6;
export const OUTLET_HIGHLIGHT_LIMIT = 5;

export function outletTableDefaultLabels(): Record<string, string> {
  return {
    covers: i18n.t('performance:metrics.covers'),
    reservations: i18n.t('performance:metrics.reservations'),
    rejected: i18n.t('performance:metrics.rejected'),
    noshow: i18n.t('performance:metrics.noshow'),
    cancel: i18n.t('performance:metrics.cancel'),
    sellitems: i18n.t('performance:metrics.sellitems'),
    seatTime: i18n.t('performance:metrics.seatTime'),
    turns: i18n.t('performance:metrics.turns'),
    occupancy: i18n.t('performance:metrics.occupancy'),
    brainAutoPct: i18n.t('performance:metrics.brainAutoPct'),
    avgCheck: i18n.t('performance:metrics.avgCheck'),
  };
}

export function guestReviewCountTip(): string {
  return i18n.t('performance:tips.guestReviewCount');
}

export type OutletTableSort = { key: string; dir: 'asc' | 'desc' };

export type OutletTableColumn =
  | { kind: 'outlet'; key: 'outlet'; label: string }
  | { kind: 'metric'; key: string; label: string; tip?: string }
  | { kind: 'reviewRating'; key: 'reviewRating'; label: string; tip: string }
  | { kind: 'reviewCount'; key: 'reviewCount'; label: string; tip: string };

export function outletTableColumnTip(col: OutletTableColumn): string | undefined {
  if (col.kind === 'outlet') return undefined;
  return col.tip;
}

export function visibleOutletTableMetricKeys(posEnabled: boolean): string[] {
  return OUTLET_TABLE_METRIC_KEYS.filter(key => {
    if (OUTLET_TABLE_HIDDEN_METRICS.has(key)) return false;
    if (key === 'avgCheck' && !posEnabled) return false;
    return true;
  });
}

export function buildOutletTableColumns(
  metricLabels: Record<string, string>,
  posEnabled: boolean,
  reviewsEnabled: boolean,
): OutletTableColumn[] {
  const defaults = outletTableDefaultLabels();
  const cols: OutletTableColumn[] = [
    { kind: 'outlet', key: 'outlet', label: i18n.t('performance:metrics.outlet') },
  ];
  for (const key of visibleOutletTableMetricKeys(posEnabled)) {
    cols.push({
      kind: 'metric',
      key,
      label: metricLabels[key] ?? defaults[key] ?? key,
      tip: PERF_MEASURE[key]?.tip,
    });
  }
  if (reviewsEnabled) {
    cols.push(
      {
        kind: 'reviewRating',
        key: 'reviewRating',
        label: i18n.t('performance:metrics.guestRating'),
        tip: i18n.t('performance:tips.guestRating'),
      },
      {
        kind: 'reviewCount',
        key: 'reviewCount',
        label: i18n.t('performance:metrics.reviews'),
        tip: guestReviewCountTip(),
      },
    );
  }
  return cols;
}

export function buildPerformanceOutletTableColumns(
  data: OpsCommandPerformancePayload,
  posEnabled: boolean,
  reviewsEnabled: boolean,
): OutletTableColumn[] {
  const labels: Record<string, string> = {};
  for (const m of data.metrics) {
    labels[m.key] = metricDisplayLabel(m.key, m.label);
  }
  return buildOutletTableColumns(labels, posEnabled, reviewsEnabled);
}

export function buildLiveOutletMetrics(
  o: Outlet,
  connected: ConnectedSourceId[],
): PerformanceMetric[] {
  const posOn = connected.includes('pos');
  const payOn = connected.includes('alenopay');
  const sellRevenue = o.sellRevenue ?? 0;

  const metric = (
    key: string,
    value: number,
    opts?: { unit?: string; locked?: boolean },
  ): PerformanceMetric => ({
    key,
    label: outletTableDefaultLabels()[key] ?? key,
    value,
    prior: 0,
    unit: opts?.unit,
    locked: opts?.locked,
  });

  return [
    metric('covers', o.coversToday),
    metric('reservations', o.bookedCount ?? 0),
    metric('rejected', 0),
    metric('noshow', o.noShow, { unit: '%' }),
    metric('cancel', o.cancelRate, { unit: '%' }),
    metric('occupancy', occupancyPct(o), { unit: '%' }),
    metric('brainAutoPct', 0, { unit: '%' }),
    metric('turns', outletTurns(o), { unit: '×' }),
    metric('seatTime', o.avgSeatMin, { unit: 'min' }),
    metric('sellitems', sellRevenue, {
      unit: '€',
      locked: !payOn && sellRevenue <= 0,
    }),
    metric('avgCheck', o.spendCover ?? 0, {
      unit: '€',
      locked: !posOn,
    }),
  ];
}

export function liveOutletReviewRow(o: Outlet): GuestReviewOutletRow | undefined {
  if (!o.review && !o.reviewVol) return undefined;
  return {
    restaurantId: o.id,
    name: o.name,
    hasTarget: false,
    platforms: [],
    avgRating: o.review ?? 0,
    reviewCount: o.reviewVol ?? (o.review ? 1 : 0),
    ratingDelta: o.reviewDelta ?? null,
    negativeSharePct: 0,
    responseRatePct: 0,
    unansweredNegative: 0,
    vsFleetAvg: 0,
  };
}

export function formatOutletMetricValue(
  m: PerformanceMetric,
  value: number,
): string {
  if (m.unit === '€') {
    return fmt.money(value);
  }
  if (m.key === 'turns' || m.unit === '×') {
    const formatted = value.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatted}×`;
  }
  const formatted = Number.isInteger(value)
    ? value.toLocaleString('de-CH')
    : value.toLocaleString('de-CH', { maximumFractionDigits: 1 });
  return `${formatted}${m.unit ?? ''}`;
}

export function outletReviewPeriodStats(
  review: GuestReviewOutletRow | undefined,
  kind: 'reviewRating' | 'reviewCount',
  comparable: boolean,
): {
  latest: number | null;
  delta: number | null;
} {
  if (!review) return { latest: null, delta: null };

  const series = review.series ?? [];
  const n = series.length;
  const hasSeries = n > 1;

  if (kind === 'reviewRating') {
    if (hasSeries && comparable) {
      const latestBucket = series[n - 1];
      const prevBucket = series[n - 2];
      const latest =
        latestBucket.reviewCount > 0 && latestBucket.avgRating > 0
          ? latestBucket.avgRating
          : null;
      const prev =
        prevBucket.reviewCount > 0 && prevBucket.avgRating > 0
          ? prevBucket.avgRating
          : null;
      return {
        latest,
        delta: latest != null && prev != null ? relDelta(latest, prev) : null,
      };
    }
    return {
      latest: review.reviewCount > 0 ? review.avgRating : null,
      delta: null,
    };
  }

  if (hasSeries && comparable) {
    const latest = series[n - 1].reviewCount;
    const prev = series[n - 2].reviewCount;
    return {
      latest,
      delta: relDelta(latest, prev),
    };
  }
  return {
    latest: review.reviewCount > 0 ? review.reviewCount : null,
    delta: null,
  };
}

function metricSortValue(
  metric: PerformanceMetric | undefined,
  _comparable: boolean,
): number | null {
  if (!metric || metric.locked) return null;
  return metric.value;
}

export function outletSortValue(
  row: OutletPerformanceRow,
  sortKey: string,
  comparable: boolean,
  review?: GuestReviewOutletRow,
): string | number | null {
  if (sortKey === 'outlet') return row.outlet.name.toLowerCase();
  if (sortKey === 'reviewRating' || sortKey === 'reviewCount') {
    return outletReviewPeriodStats(review, sortKey, comparable).latest;
  }
  const metricByKey = Object.fromEntries(
    (row.metrics ?? []).map(m => [m.key, m]),
  );
  return metricSortValue(metricByKey[sortKey], comparable);
}

export function liveOutletSortValue(
  o: Outlet,
  sortKey: string,
  metrics: PerformanceMetric[],
  review?: GuestReviewOutletRow,
): string | number | null {
  if (sortKey === 'outlet') return o.name.toLowerCase();
  if (o.closed && sortKey !== 'outlet') return null;
  if (sortKey === 'reviewRating' || sortKey === 'reviewCount') {
    return outletReviewPeriodStats(review, sortKey, false).latest;
  }
  const metricByKey = Object.fromEntries(metrics.map(m => [m.key, m]));
  return metricSortValue(metricByKey[sortKey], false);
}

export function compareSortValues(
  a: string | number | null,
  b: string | number | null,
  dir: 'asc' | 'desc',
): number {
  const aMissing = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bMissing = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === 'string' && typeof b === 'string') {
    const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  }
  const cmp = (a as number) - (b as number);
  return dir === 'asc' ? cmp : -cmp;
}

export function outletMetricDisplayDelta(
  metric: PerformanceMetric | undefined,
  comparable: boolean,
): { value: number | null; suffix: string } | null {
  const values = outletMetricPeriodValues(metric, comparable);
  if (!metric || !values) return null;
  return scorecardPeriodDelta({
    key: metric.key,
    value: values.latest,
    prior: values.prev,
  });
}

export function outletMetricPeriodDelta(
  metric: PerformanceMetric | undefined,
  comparable: boolean,
): number | null {
  if (!metric || metric.locked || !comparable) return null;
  const values = outletMetricPeriodValues(metric, comparable);
  if (!values) return null;
  return relDelta(values.latest, values.prev);
}

export function outletMetricPeriodValues(
  metric: PerformanceMetric | undefined,
  comparable: boolean,
): { latest: number; prev: number } | null {
  // Match ScoreCard: selected period total vs compare-window prior
  // (global frame settings), not latest aggregation bucket vs the one before.
  if (!metric || metric.locked || !comparable) return null;
  const latest = metric.value;
  const prev = metric.prior;
  if (!Number.isFinite(latest) || !Number.isFinite(prev)) return null;
  return { latest, prev };
}

function clampTrend(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Scale impact by how much volume sits behind a bucket (thin buckets = noisy). */
export function outletTrendVolumeScale(
  prev: number,
  latest: number,
  floor = OUTLET_TREND_VOLUME_FLOOR,
): number {
  const base = Math.max(prev, latest, 0);
  if (base < floor) return clampTrend(base / floor, 0.15, 1);
  return clampTrend(Math.sqrt(base / floor), 0.5, 1.5);
}

export function outletMetricTrendContribution(
  metric: PerformanceMetric | undefined,
  comparable: boolean,
  meta: OutletTrendMeta,
  volumeAnchor?: { prev: number; latest: number },
): number | null {
  const values = outletMetricPeriodValues(metric, comparable);
  if (!values) return null;
  const { latest, prev } = values;

  let impact: number;
  switch (meta.kind) {
    case 'volume': {
      const rel = relDelta(latest, prev);
      if (rel == null) return null;
      const directed = meta.invert ? -rel : rel;
      impact = clampTrend(
        directed * outletTrendVolumeScale(prev, latest),
        -55,
        55,
      );
      break;
    }
    case 'rate': {
      const pp = latest - prev;
      const directed = meta.invert ? -pp : pp;
      impact = clampTrend(directed, -12, 12);
      break;
    }
    case 'duration': {
      if (prev <= 0 && latest <= 0) return null;
      // Minutes saved (positive) = good momentum; cap so one outlier cannot dominate.
      const minuteDelta = prev - latest;
      impact = clampTrend(minuteDelta / 4, -18, 18);
      break;
    }
    case 'ratio': {
      const rel = relDelta(latest, prev);
      if (rel == null) return null;
      const directed = meta.invert ? -rel : rel;
      const scale = volumeAnchor
        ? outletTrendVolumeScale(volumeAnchor.prev, volumeAnchor.latest)
        : 1;
      impact = clampTrend(directed * scale, -35, 35);
      break;
    }
  }

  return meta.importance * impact;
}

export function outletReviewTrendContribution(
  review: GuestReviewOutletRow | undefined,
  kind: 'reviewRating' | 'reviewCount',
  comparable: boolean,
  meta: OutletTrendMeta,
): number | null {
  const { latest, prev, delta } = outletReviewPeriodStatsWithPrev(
    review,
    kind,
    comparable,
  );
  if (latest == null || prev == null || delta == null) return null;

  let impact: number;
  if (meta.kind === 'volume') {
    impact = clampTrend(
      delta * outletTrendVolumeScale(prev, latest, 3),
      -55,
      55,
    );
  } else {
    impact = clampTrend(delta, -12, 12);
  }
  return meta.importance * impact;
}

function outletReviewPeriodStatsWithPrev(
  review: GuestReviewOutletRow | undefined,
  kind: 'reviewRating' | 'reviewCount',
  comparable: boolean,
): {
  latest: number | null;
  prev: number | null;
  delta: number | null;
} {
  if (!review) return { latest: null, prev: null, delta: null };

  const series = review.series ?? [];
  const n = series.length;
  const hasSeries = n > 1;

  if (kind === 'reviewRating') {
    if (hasSeries && comparable) {
      const latestBucket = series[n - 1];
      const prevBucket = series[n - 2];
      const latest =
        latestBucket.reviewCount > 0 && latestBucket.avgRating > 0
          ? latestBucket.avgRating
          : null;
      const prev =
        prevBucket.reviewCount > 0 && prevBucket.avgRating > 0
          ? prevBucket.avgRating
          : null;
      return {
        latest,
        prev,
        delta: latest != null && prev != null ? latest - prev : null,
      };
    }
    return { latest: null, prev: null, delta: null };
  }

  if (hasSeries && comparable) {
    const latest = series[n - 1].reviewCount;
    const prev = series[n - 2].reviewCount;
    return {
      latest,
      prev,
      delta: relDelta(latest, prev),
    };
  }
  return { latest: null, prev: null, delta: null };
}

export function signedOutletMetricDelta(
  metric: PerformanceMetric | undefined,
  comparable: boolean,
): number | null {
  const delta = outletMetricPeriodDelta(metric, comparable);
  if (delta == null) return null;
  return OUTLET_TABLE_INVERT_METRICS.has(metric!.key) ? -delta : delta;
}

export function outletReviewSignedDelta(
  review: GuestReviewOutletRow | undefined,
  kind: 'reviewRating' | 'reviewCount',
  comparable: boolean,
): number | null {
  const { delta } = outletReviewPeriodStats(review, kind, comparable);
  return delta;
}

export type OutletTrendScoreResult = {
  score: number;
  metricCount: number;
};

export function outletTrendScore(
  row: OutletPerformanceRow,
  comparable: boolean,
  visibleMetricKeys: string[],
  review?: GuestReviewOutletRow,
): OutletTrendScoreResult | null {
  if (!comparable) return null;

  const metricByKey = Object.fromEntries(
    (row.metrics ?? []).map(m => [m.key, m]),
  );
  const volumeAnchor =
    outletMetricPeriodValues(metricByKey.covers, comparable) ??
    outletMetricPeriodValues(metricByKey.reservations, comparable);

  let weightedSum = 0;
  let weightTotal = 0;
  let metricCount = 0;
  let hasDemandSignal = false;

  for (const key of visibleMetricKeys) {
    const meta = OUTLET_TREND_META[key];
    if (!meta) continue;

    let contribution: number | null;
    if (key === 'reviewRating' || key === 'reviewCount') {
      contribution = outletReviewTrendContribution(review, key, comparable, meta);
    } else {
      contribution = outletMetricTrendContribution(
        metricByKey[key],
        comparable,
        meta,
        meta.kind === 'ratio' ? (volumeAnchor ?? undefined) : undefined,
      );
    }
    if (contribution == null) continue;

    if (OUTLET_TREND_DEMAND_KEYS.has(key)) hasDemandSignal = true;
    weightedSum += contribution;
    weightTotal += meta.importance;
    metricCount += 1;
  }

  if (
    !hasDemandSignal ||
    weightTotal === 0 ||
    metricCount < OUTLET_TREND_MIN_METRICS
  ) {
    return null;
  }
  return { score: weightedSum / weightTotal, metricCount };
}

export type OutletTrendHighlights<T> = {
  top: T[];
  bottom: T[];
  eligibleCount: number;
};

export function pickOutletTrendHighlights<T>(
  rows: T[],
  scoreFn: (row: T) => number | null,
  getId: (row: T) => string,
  limit = OUTLET_HIGHLIGHT_LIMIT,
): OutletTrendHighlights<T> {
  const scored = rows
    .map(row => ({ row, score: scoreFn(row) }))
    .filter((x): x is { row: T; score: number } => x.score != null)
    .sort((a, b) => b.score - a.score || 0);

  const top = scored.slice(0, limit).map(x => x.row);
  const topIds = new Set(top.map(getId));
  const bottom = [...scored]
    .reverse()
    .filter(x => !topIds.has(getId(x.row)))
    .slice(0, limit)
    .map(x => x.row);

  return { top, bottom, eligibleCount: scored.length };
}

export function canShowOutletTrendHighlights(
  eligibleCount: number,
  comparable: boolean,
): boolean {
  return comparable && eligibleCount >= OUTLET_TREND_MIN_ELIGIBLE;
}

export function OutletTableSectionRow({
  label,
  colSpan,
  tone = 'neutral',
}: {
  label: string;
  colSpan: number;
  tone?: 'top' | 'bottom' | 'neutral';
}) {
  return (
    <tr className={`optbl-section-row optbl-section-${tone}`}>
      <td colSpan={colSpan} className="optbl-section-cell">
        {label}
      </td>
    </tr>
  );
}

export function OutletTableToggle({
  expanded,
  totalCount,
  onToggle,
}: {
  expanded: boolean;
  totalCount: number;
  onToggle: () => void;
}) {
  const { t } = useTranslation('performance');
  return (
    <div className="optbl-hint optbl-toggle-bar">
      <button type="button" className="optbl-toggle" onClick={onToggle}>
        {expanded
          ? t('table.showHighlights')
          : t('table.showAllOutlets', { count: totalCount })}
      </button>
    </div>
  );
}

export function sortOutletRows(
  rows: OutletPerformanceRow[],
  sort: OutletTableSort,
  comparable: boolean,
  reviewByOutletId: Map<string, GuestReviewOutletRow>,
): OutletPerformanceRow[] {
  return [...rows].sort((a, b) => {
    const av = outletSortValue(
      a,
      sort.key,
      comparable,
      reviewByOutletId.get(a.outlet.id),
    );
    const bv = outletSortValue(
      b,
      sort.key,
      comparable,
      reviewByOutletId.get(b.outlet.id),
    );
    const primary = compareSortValues(av, bv, sort.dir);
    if (primary !== 0) return primary;
    return a.outlet.name.localeCompare(b.outlet.name, undefined, {
      sensitivity: 'base',
    });
  });
}

export function OutletReviewCell({
  review,
  kind,
  comparable,
}: {
  review?: GuestReviewOutletRow;
  kind: 'reviewRating' | 'reviewCount';
  comparable: boolean;
}) {
  const { latest, delta } = outletReviewPeriodStats(review, kind, comparable);

  if (kind === 'reviewRating') {
    if (latest == null) {
      return <td className="opt-metric-td locked">-</td>;
    }
    return (
      <td className="opt-metric-td">
        <span className="opt-metric-val">{latest.toFixed(1)}★</span>
        {comparable && delta != null && (
          <Delta value={delta} size={11} />
        )}
      </td>
    );
  }

  if (latest == null) {
    return <td className="opt-metric-td locked">-</td>;
  }

  return (
    <td className="opt-metric-td">
      <span className="opt-metric-val">{fmt.num(latest)}</span>
      {comparable && delta != null && (
        <Delta value={delta} size={11} />
      )}
    </td>
  );
}

export function OutletMetricCell({
  metric,
  comparable,
}: {
  metric: PerformanceMetric | undefined;
  comparable: boolean;
}) {
  const { t } = useTranslation('performance');
  if (!metric || metric.locked) {
    const source = metric ? perfConnectSource(metric.key) : undefined;
    return (
      <td
        className="opt-metric-td locked"
        title={source ? t('tips.connectToUnlock', { source }) : t('tips.notAvailable')}
      >
        -
      </td>
    );
  }
  const period = outletMetricPeriodValues(metric, comparable);
  const displayValue = period?.latest ?? metric.value;
  const delta = outletMetricDisplayDelta(metric, comparable);
  const invert = OUTLET_TABLE_INVERT_METRICS.has(metric.key);
  return (
    <td className="opt-metric-td">
      <span className="opt-metric-val">
        {formatOutletMetricValue(metric, displayValue)}
      </span>
      {comparable && delta != null && (
        <Delta
          value={delta.value}
          suffix={delta.suffix}
          invert={invert}
          size={11}
        />
      )}
    </td>
  );
}

export function OutletTableHeader({
  columns,
  sort,
  onSort,
  sortDisabled = false,
}: {
  columns: OutletTableColumn[];
  sort: OutletTableSort;
  onSort: (next: OutletTableSort) => void;
  sortDisabled?: boolean;
}) {
  return (
    <thead>
      <tr>
        {columns.map(col => {
          const active = sort.key === col.key;
          const sortable = !sortDisabled;
          const tip = outletTableColumnTip(col);
          return (
            <th
              key={col.key}
              className={`${col.kind === 'outlet' ? 'opt-metric-out sm-th-out' : 'opt-metric-col sm-th-col'}${sortable ? ' sortable' : ''}${active && sortable ? ' on' : ''}`}
              onClick={
                sortable
                  ? () =>
                      onSort(
                        sort.key === col.key
                          ? {
                              key: col.key,
                              dir: sort.dir === 'asc' ? 'desc' : 'asc',
                            }
                          : { key: col.key, dir: 'desc' },
                      )
                  : undefined
              }
            >
              <span className="opt-th-inner">
                <span className="opt-th-label">{col.label}</span>
                {tip ? (
                  <ScoreCardHelp
                    text={tip}
                    iconSize={12}
                    className="sc-help-compact"
                  />
                ) : null}
                {sortable ? (
                  <span className="opt-sort-ind" aria-hidden>
                    {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                ) : null}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}
